import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getFormattedUserTimeZone, parseShipmentDayjs } from './utils';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export interface TimeFormatResult {
  relative: string;
  full: string;
}

/**
 * Returns relative and full formatted time string.
 * Example full: "Aug 22, 2025, 6:30 PM UTC"
 */
export const getFormattedTime = (
  ts: string,
  isTimeZone = false,
  dateFormat?: string | null
): TimeFormatResult => {
  if (!ts) return { relative: '---', full: '---' };

  const tzString = getFormattedUserTimeZone();
  const match = tzString.match(/ ([A-Za-z_/]+)$/);
  const userTz = match ? match[1] : dayjs.tz.guess();

  const now = isTimeZone ? dayjs().tz(userTz) : dayjs();
  const naive = parseShipmentDayjs(ts, dateFormat);
  const created = isTimeZone
    ? naive
      ? dayjs.tz(naive.format('YYYY-MM-DDTHH:mm:ss'), userTz)
      : dayjs.tz(ts, userTz)
    : (naive ?? dayjs(ts, 'YYYY-MM-DD HH:mm:ss'));

  const msDiff = now.diff(created);
  const seconds = Math.floor(msDiff / 1000);
  const minutes = Math.floor(msDiff / (60 * 1000));
  const hours = Math.floor(msDiff / (60 * 60 * 1000));
  const days = Math.floor(msDiff / (24 * 60 * 60 * 1000));

  let relative: string;
  if (seconds < 60) relative = 'Now';
  else if (minutes < 60) relative = `${minutes} m ago`;
  else if (hours < 24) relative = `${hours} hrs ago`;
  else relative = `${days} days ago`;

  const tzAbbr = created.format('z') || userTz.split('/').pop();
  const dateTemplate =
    dateFormat === 'YYYY/MM/DD'
      ? 'YYYY, MMM D, h:mm A'
      : dateFormat === 'DD/MM/YYYY'
        ? 'D MMM, YYYY, h:mm A'
        : 'MMM D, YYYY, h:mm A';
  const full = `${created.format(dateTemplate)} ${tzAbbr}`;

  return { relative, full };
};

const TIMEZONE_ABBREVIATIONS: Record<string, string> = {
  'Pacific/Pago_Pago': 'SST',
  'Pacific/Honolulu': 'HST',
  'America/Anchorage': 'AKST',
  'America/Los_Angeles': 'PST',
  'America/Denver': 'MST',
  'America/Chicago': 'CST',
  'America/New_York': 'EST',
  'America/Halifax': 'AST',
  'America/Sao_Paulo': 'BRT',
  'Atlantic/Azores': 'AZOT',
  'Europe/London': 'GMT',
  'Europe/Berlin': 'CET',
  'Europe/Athens': 'EET',
  'Africa/Nairobi': 'EAT',
  'Asia/Dubai': 'GST',
  'Asia/Karachi': 'PKT',
  'Asia/Kolkata': 'IST',
  'Asia/Dhaka': 'BST',
  'Asia/Bangkok': 'ICT',
  'Asia/Shanghai': 'CST',
  'Asia/Tokyo': 'JST',
  'Australia/Sydney': 'AEST',
  'Pacific/Noumea': 'NCT',
  'Pacific/Auckland': 'NZST',
  'Pacific/Tongatapu': 'TOT',
  'Pacific/Kiritimati': 'LINT',
};

const mapUserTimeZone = (tz: string): string => {
  const tzMap: Record<string, string> = {
    'Pacific/Pago_Pago': 'Pacific/Pago_Pago',
    'US/Pacific': 'America/Los_Angeles',
    'US/Central': 'America/Chicago',
    'US/Eastern': 'America/New_York',
  };
  return tzMap[tz] || tz;
};

export const getTimeZoneAbbreviation = (
  dateStr: string | undefined | null,
  userTimeZone: string
): string => {
  if (!dateStr) return '';

  try {
    const tz = mapUserTimeZone(userTimeZone);
    const parsed = parseShipmentDayjs(dateStr);
    const date = parsed?.toDate() ?? new Date(dateStr);

    if (isNaN(date.getTime())) return '';

    const options: Intl.DateTimeFormatOptions = {
      timeZone: tz,
      timeZoneName: 'short',
    };
    const formatted = new Intl.DateTimeFormat('en-US', options).format(date);

    const match = formatted.match(/\b([A-Z]{2,4})\b$/);
    if (match) return match[1];

    if (TIMEZONE_ABBREVIATIONS[tz]) return TIMEZONE_ABBREVIATIONS[tz];
    const offset = -date.getTimezoneOffset() / 60;
    return `GMT${offset >= 0 ? '+' : ''}${offset}`;
  } catch (err) {
    console.warn(err);
    return '';
  }
};
